import React from 'react'
import { makeStyles } from '@material-ui/core/styles'

import Image from 'next/image'
import { Typography, Grid, Accordion, AccordionSummary, Button, Hidden } from '@material-ui/core'
import ArrowDropDownRoundedIcon from '@material-ui/icons/ArrowDropDownRounded'

// types
import { StakingPlatform } from '../../../../../types/stakingPlatform'
import { formatNumber, shortenNumber } from '../../../../utils/number'

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: '12px',
    padding: '0',
    alignItems: 'center',
    border: 0,
    [theme.breakpoints.up('md')]: {
      padding: '1rem 0',
    },
  },
  imageWrapper: {
    padding: '.4rem',
    background: 'white',
    borderRadius: '.6rem',
    width: '2.8rem',
    height: '2.8rem',
    margin: '.8rem 0 .4rem 0',
    [theme.breakpoints.up('md')]: {
      margin: '0 .4rem',
    },
  },
  imageContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    position: 'relative',
    overflow: 'hidden',
  },
  detailsContainer: {
    width: '100%',
    textAlign: 'center',
    '& > p': {
      margin: '.4rem 0',
      fontSize: '.8rem',
      [theme.breakpoints.up('md')]: {},
    },
    [theme.breakpoints.up('md')]: {
      textAlign: 'left',
      width: '60%',
    },
  },
  accordionSummary: {
    padding: '1rem 2rem',
    width: '100%',
    borderRadius: '1rem',
    border: '1px solid rgba(0, 0, 0, 0) !important',
    '&.expanded': {
      border: '1px solid rgba(0, 0, 0, 0.05) !important',
      '& $accordionSummaryContent': {
        margin: '0 !important',
      },
    },
    '& p': {
      fontSize: '1rem',
      fontWeight: '600',
      color: 'rgba(43, 24, 65, .8)',
    },
    '&.odd': {
      background: 'rgba(0, 0, 0, 0.04)',
    },
  },
  accordionSummaryContent: {
    margin: 0,
  },
  securityScore: {
    textAlign: 'center',
    [theme.breakpoints.down('md')]: {
      textAlign: 'left',
    },
  },
  price: {},
  feesContainer: {},
  starting: {},
  dropDownIconContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '& > svg': {
      transform: 'rotateZ(0deg)',
      transition: 'transform 0.2s ease-in-out',
    },
    '&.expanded > svg': {
      transform: 'rotateZ(-180deg)',
      transition: 'transform 0.2s ease-in-out',
    },
  },
  number: {
    [theme.breakpoints.up('md')]: {
      // margin: '0 1rem',
    },
  },
  title: {
    textOverflow: 'ellipsis',
  },
  numberContainer: {},
  imageGrid: {
    gap: '.4rem',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
  accordionDetailsContainer: {
    marginTop: '1rem',
    padding: '4rem 2rem',
  },

  actionsColumn: {
    // flexDirection: 'column',
    alignItems: 'center',
    '& a': {
      color: 'black',
      fontSize: '.8rem',
      borderBottom: '1px solid',
      paddingBottom: '.4rem',
      // margin: '1rem 1rem',
      width: 'fit-content',
    },
    '& a.MuiButton-root': {
      color: 'white',
      textTransform: 'none',
    },
  },
  descriptionContainer: {
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '50%',
    },
    '& p': {
      fontWeight: '400',
      textAlign: 'center',
    },
  },
  ctaContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
  label: {
    '&p': {
      fontWeight: '400 !important',
      fontSize: '.8rem !important',
    },
  },
}))

interface props {
  platform: StakingPlatform
  number?: number
  tvl?: number
  showTvl?: boolean
  ethPrice?: number
}

function ExchangeCard({ platform: platform, number, tvl, showTvl, ethPrice }: props): JSX.Element {
  const classes = useStyles({})

  const [expanded, setExpanded] = React.useState(false)

  const handleChange = () => {
    setExpanded(!expanded)
  }

  return (
    <Accordion className={classes.root} onChange={handleChange}>
      <AccordionSummary
        classes={{
          root: `
          ${classes.accordionSummary} 
          ${number && number % 2 === 0 ? 'even' : 'odd'} 
          ${expanded ? 'expanded' : ''}`,
          content: classes.accordionSummaryContent,
        }}
      >
        <Grid container alignItems="center" spacing={6}>
          <Grid item container alignItems="center" xs={6} md={3}>
            <Grid
              container
              item
              xs={12}
              md={3}
              alignItems="center"
              className={classes.numberContainer}
              justify="flex-start"
            >
              <div className={`${classes.dropDownIconContainer} ${expanded ? 'expanded' : ''}`}>
                <ArrowDropDownRoundedIcon />
              </div>
              <Typography className={classes.number} variant="body2">
                {number && `${number}.`}
              </Typography>
            </Grid>
            <Grid
              item
              container
              xs={12}
              md={9}
              alignItems="center"
              justify="flex-start"
              className={classes.imageGrid}
            >
              <div className={classes.imageWrapper}>
                <div className={classes.imageContainer}>
                  <Image
                    src={platform.logo ? platform.logo.url : '/svg/wallet.svg'}
                    objectFit="contain"
                    layout="fill"
                  />
                </div>
              </div>
              <Typography className={classes.title} variant="body2">
                {platform.name}
              </Typography>
            </Grid>
          </Grid>
          <Grid item xs={6} md={2}>
            <Hidden mdUp>
              <Typography
                style={{
                  fontSize: '.8rem',
                  fontWeight: 400,
                }}
                className={classes.label}
                variant="body2"
              >
                Fee.
              </Typography>
            </Hidden>
            <Typography className={classes.securityScore} variant="body2">
              {platform.fee}
            </Typography>
          </Grid>
          <Grid item xs={6} md={1}>
            <Hidden mdUp>
              <Typography
                style={{
                  fontSize: '.8rem',
                  fontWeight: 400,
                }}
                className={classes.label}
                variant="body2"
              >
                Custodial
              </Typography>
            </Hidden>
            <Typography className={classes.price} variant="body2">
              {platform.custodial ? 'No' : 'Yes'}
            </Typography>
          </Grid>
          <Grid item xs={6} md={2}>
            <Hidden mdUp>
              <Typography
                style={{
                  fontSize: '.8rem',
                  fontWeight: 400,
                }}
                className={classes.label}
                variant="body2"
              >
                Min Deposit
              </Typography>
            </Hidden>
            <Typography className={classes.starting} variant="body2">
              {platform.min_deposit}
            </Typography>
          </Grid>
          {showTvl && (
            <Grid item xs={12} md={2}>
              <Hidden mdUp>
                <Typography
                  style={{
                    fontSize: '.8rem',
                    fontWeight: 400,
                  }}
                  className={classes.label}
                  variant="body2"
                >
                  TVL
                </Typography>
              </Hidden>
              <Typography className={classes.starting} variant="body2">
                {tvl
                  ? ethPrice
                    ? `$${shortenNumber(tvl * ethPrice)}`
                    : `${formatNumber(tvl)} ETH`
                  : '-'}
              </Typography>
            </Grid>
          )}
          {/* <Hidden smDown> */}
          <Grid
            container
            alignItems="center"
            justify="flex-end"
            item
            md={2}
            xs={12}
            className={classes.actionsColumn}
          >
            <Button
              variant="contained"
              color="primary"
              disableElevation
              target="_blank"
              href={platform.url || '#'}
            >
              Go to website {'->'}
            </Button>
          </Grid>
          {/* </Hidden> */}
        </Grid>
      </AccordionSummary>
    </Accordion>
  )
}

export default ExchangeCard
